export default function SafetyHealthSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Safety and <span className="text-teal-600">Health Considerations</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Hoarder houses often present serious health and safety hazards. We have the expertise and equipment to handle these situations properly.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-alert-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Structural Hazards</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Excessive weight from accumulated items can compromise floors, ceilings, and walls. Blocked exits create fire hazards and prevent safe evacuation.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Floor reinforcement assessment</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Ceiling and wall integrity checks</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Safe removal procedures</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-virus-line text-2xl text-orange-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Biological Hazards</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Mold, bacteria, rodent droppings, and other biohazards require specialized handling and protective equipment.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Professional mold remediation</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Biohazard cleanup protocols</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Air quality restoration</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="w-14 h-14 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
              <i className="ri-bug-line text-2xl text-yellow-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Pest Infestations</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Rodents, insects, and other pests often thrive in hoarder environments, creating health risks and property damage.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Professional pest control</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Fumigation when necessary</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                <span>Prevention measures</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-10 lg:p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">Professional Safety Protocols</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Our team follows strict safety protocols when working in hoarder houses. We use appropriate personal protective equipment, follow OSHA guidelines, and work with licensed professionals for specialized hazards.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Licensed & Insured</h4>
                    <p className="text-gray-300 text-sm">Our team is fully licensed and insured for hazardous material handling and property restoration work.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-team-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Trained Professionals</h4>
                    <p className="text-gray-300 text-sm">Every team member receives specialized training in hoarding cleanup, safety protocols, and compassionate service.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-recycle-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Proper Disposal</h4>
                    <p className="text-gray-300 text-sm">We follow all local, state, and federal regulations for proper disposal of hazardous materials and waste.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-full min-h-[400px]">
              <img 
                src="https://public.readdy.ai/ai/img_res/66c838be1ca541cfe8ddeccb073fdbe4.jpg"
                alt="Professional safety equipment and protocols"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
